import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, createElement } from "react";
import styled from "styled-components";
import { IconButton, TextButton } from "@creditornot/cb-button";
import { colors, elevationCss, typographyCss } from "@creditornot/cb-ingredients";
import { createThemedCssVarDefinitions, createCssVars } from "@creditornot/cb-utils";
const AlertCircleFilled = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M11.984.003a12.205 12.205 0 00-8.51 3.652A11.799 11.799 0 00.002 12.21 11.778 11.778 0 0011.8 24h.214c6.677-.069 12.039-5.53 11.985-12.208A11.765 11.765 0 0011.984.003zM10.5 16.543a1.476 1.476 0 011.449-1.53h.027c.82.002 1.492.65 1.522 1.47a1.475 1.475 0 01-1.448 1.53h-.027a1.529 1.529 0 01-1.523-1.47zm.5-4.041v-6a1 1 0 012 0v6a1 1 0 01-2 0z" }) }));
const AlertCircleFilledIcon = AlertCircleFilled;
const Checkmark = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M23.2665 2.33434C24.0619 2.90197 24.2377 3.99484 23.6593 4.77533L11.8707 21.2804C11.582 21.6699 11.1422 21.9252 10.6552 21.986C10.1683 22.0468 9.67734 21.9077 9.29841 21.6015L0.648785 14.6117C-0.110418 13.9982 -0.219027 12.897 0.406198 12.152C1.03142 11.407 2.15372 11.3004 2.91293 11.914L10.1027 17.7239L20.7789 2.71976C21.3573 1.93927 22.4711 1.76671 23.2665 2.33434Z" }) }));
const CheckmarkIcon = Checkmark;
const SvgCross = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M2.44 19.44a1.5 1.5 0 002.121 2.121l7.262-7.26a.25.25 0 01.354 0l7.262 7.262a1.5 1.5 0 102.122-2.12l-7.26-7.266a.25.25 0 010-.354l7.262-7.262a1.5 1.5 0 00-2.124-2.12L12.177 9.7a.25.25 0 01-.354 0L4.561 2.44A1.5 1.5 0 002.44 4.56L9.7 11.823a.25.25 0 010 .354L2.44 19.44z" }) }));
const CrossIcon = SvgCross;
const InfoCircleFilled = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12C23.993 5.376 18.624.007 12 0zm.25 5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm2.25 13.5h-4a1 1 0 010-2h.75a.25.25 0 00.25-.25v-4.5a.25.25 0 00-.25-.25h-.75a1 1 0 010-2h1a2 2 0 012 2v4.75c0 .138.112.25.25.25h.75a1 1 0 010 2z" }) }));
const InfoCircleFilledIcon = InfoCircleFilled;
const ErrorIcon = styled(AlertCircleFilledIcon)`
  display: block;
  width: 20px;
  fill: ${colors.strawberry100};
`;
const InfoIcon = styled(InfoCircleFilledIcon)`
  ${createThemedCssVarDefinitions({
  fill: {
    light: colors.pepper100,
    dark: colors.salt100
  }
})}
  display: block;
  width: 20px;
  fill: var(--fill);
`;
const SuccessIconBG = styled.div`
  ${createThemedCssVarDefinitions({
  fill: {
    light: colors.salt100,
    dark: colors.pepper100
  }
})}
  width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.lime100};

  svg {
    width: 12px;
    fill: var(--fill);
  }
`;
const SuccessIcon = () => /* @__PURE__ */ jsx(SuccessIconBG, { children: /* @__PURE__ */ jsx(CheckmarkIcon, {}) });
const WarningIcon = styled(AlertCircleFilledIcon)`
  display: block;
  width: 20px;
  fill: ${colors.banana100};
`;
const variantConfigs = {
  error: {
    icon: ErrorIcon
  },
  info: {
    icon: InfoIcon
  },
  success: {
    icon: SuccessIcon
  },
  warning: {
    icon: WarningIcon
  }
};
const colorCssVars = createCssVars(["background", "text"]);
const Root = styled.div`
  ${createThemedCssVarDefinitions({
  background: {
    light: colors.salt100,
    dark: colors.pepper100
  },
  text: {
    light: colors.pepper100,
    dark: colors.salt100
  }
})}

  ${({ $level }) => elevationCss($level)}
  min-width: 100px;
  display: inline-block;
  box-sizing: border-box;
  border-radius: 8px;
  flex-shrink: 0;
  background-color: ${colorCssVars.background};
  color: ${colorCssVars.text};
  padding: 16px;
  max-width: 400px;
  width: 100%;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: stretch;
`;
const IconContainer = styled.div`
  flex-shrink: 0;
  padding-inline-end: 16px;
  display: flex;
  align-items: center;
`;
const TitleContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
`;
const Title = styled.div`
  ${typographyCss.Body3()};
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const CloseButtonContainer = styled.div`
  flex-shrink: 0;
  padding-inline-start: 16px;
  display: flex;
  align-items: center;
`;
const Content = styled.div`
  margin-top: 16px;
  ${typographyCss.Body3()};
`;
const Buttons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
`;
const Toast = ({
  children,
  closeButtonAriaLabel = "Close notification",
  closeButtonProps,
  elevationLevel = 24,
  onCloseButtonClick,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  primaryButtonLabel,
  secondaryButtonLabel,
  title,
  variant = "info",
  ...rest
}) => /* @__PURE__ */ jsxs(Root, { $level: elevationLevel, ...rest, children: [
  /* @__PURE__ */ jsxs(TitleRow, { children: [
    /* @__PURE__ */ jsx(IconContainer, { children: createElement(variantConfigs[variant].icon) }),
    /* @__PURE__ */ jsx(TitleContainer, { children: /* @__PURE__ */ jsx(Title, { children: title }) }),
    /* @__PURE__ */ jsx(CloseButtonContainer, { children: /* @__PURE__ */ jsx(
      IconButton,
      {
        ...closeButtonProps,
        alignment: "iconEdges",
        "aria-label": closeButtonAriaLabel,
        background: "animated",
        onClick: onCloseButtonClick,
        size: "small",
        variant: "grey",
        children: /* @__PURE__ */ jsx(CrossIcon, {})
      }
    ) })
  ] }),
  /* @__PURE__ */ jsx(Content, { children }),
  primaryButtonLabel && /* @__PURE__ */ jsxs(Buttons, { children: [
    secondaryButtonLabel && /* @__PURE__ */ jsx(TextButton, { onClick: onSecondaryButtonClick, size: "small", variant: "grey", children: secondaryButtonLabel }),
    /* @__PURE__ */ jsx(TextButton, { onClick: onPrimaryButtonClick, size: "small", variant: "blue", children: primaryButtonLabel })
  ] })
] });
export {
  Toast
};
//# sourceMappingURL=index.js.map
